// 流式聊天相关功能
import { atom } from 'jotai';
import { supabase } from '../../../lib/supabase';
import {
  Message,
  MessageContent,
  InitEvent,
  ContentBlockStartEvent,
  ContentBlockDeltaEvent,
  MessageStopEvent,
  ToolUseStartEvent,
  ToolResultEvent,
  ToolUseStopEvent,
  INITIAL_PLACEHOLDER_ID,
  ProcessLogState,
  ProcessLogStep,
  ToolRequestContent,
  Conversation
} from './types';
import {
  messagesAtom,
  selectedConversationIdAtom,
  conversationsAtom
} from './baseAtoms';
import {
  logStep,
  updateOrAddLlmStep,
  updateToolRequestStepStatus,
  messageProcessLogAtom
} from './processLogAtoms';
import { fetchConversationsAtom } from './conversationAtoms';
import { v4 as uuidv4 } from 'uuid';

/**
 * 处理流式聊天请求
 */
export const handleStreamingChatRequest = async (
  get: any,
  set: any,
  requestBody: any,
  userMessage: Message,
  initialPlaceholderMessage: Message,
  isTempNewConversation: boolean,
  currentConversationId: string | null
): Promise<{ // Define a return type (adjust fields as needed)
  conversation: { id: string };
  userMessage: { id: string | null }; // Allow null for userMessage ID
  aiMessageId: string; // Expecting a string ID back
  conversationId: string;
}> => {
  // Update messagesAtom: Add user message and the placeholder (which now has a temp final ID)
  set(messagesAtom, (prevMessages: Message[]) => {
    const userMessageExists = prevMessages.some(msg => msg.id === userMessage.id);
    const messagesWithUser = userMessageExists ? prevMessages : [...prevMessages, userMessage];
    // Add placeholder (it already has its temp final ID and stable key)
    return [...messagesWithUser, initialPlaceholderMessage];
  });

  // 获取当前会话
  const session = await supabase.auth.getSession();
  const accessToken = session.data.session?.access_token;

  // 添加自定义请求头
  const headers = {
    'Content-Type': 'application/json',
    'Authorization': accessToken ? `Bearer ${accessToken}` : '',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    'Accept': 'text/event-stream' // 明确指定接受SSE流
  };

  // 构建API URL，使用 VITE_BACKEND_API_URL
  const baseApiUrl = import.meta.env.VITE_BACKEND_API_URL.replace(/\/$/, ''); // Get base URL from env
  const apiUrl = `${baseApiUrl}/chat/stream/combined`;
  console.log(`[sendMessageAtom] Streaming request to: ${apiUrl}`); // Log the target URL

  // 先发送POST请求初始化流式聊天
  const response = await fetch(apiUrl, {
    method: 'POST',
    headers,
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    throw new Error(`初始化流式聊天失败: ${response.status} ${response.statusText}`);
  }

  // 手动模拟EventSource的行为
  const reader = response.body?.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  // 模拟EventSource的行为
  const eventSource = {
    onmessage: null as ((event: any) => void) | null,
    addEventListener: (type: string, callback: (event: any) => void) => {
      // 存储事件监听器
      if (!eventListeners[type]) {
        eventListeners[type] = [];
      }
      eventListeners[type].push(callback);
    },
    close: () => {
      // 停止读取流
      isReading = false;
      if (reader) {
        reader.cancel().catch(err => console.error('Error canceling reader:', err));
      }
    }
  };

  // 存储事件监听器
  const eventListeners: Record<string, ((event: any) => void)[]> = {};

  // 标记是否正在读取
  let isReading = true;

  // 定义变量存储从后端获取的信息
  let actualConversationId: string | null = null;
  let userMessageId: string | null = null;
  let assistantMessageId: string | null = null; // REAL ID from backend
  let finalAiMessageId: string | null = initialPlaceholderMessage.id; // Initialize with the known temp ID
  const streamInstanceId = uuidv4(); // Unique ID for this specific streaming request for easier log tracking
  console.log(`[${streamInstanceId}] handleStreamingChatRequest initiated. Temp Final ID: ${finalAiMessageId}, Stable Key: ${initialPlaceholderMessage.metadata?.stableKey}`);

  // 开始读取流
  if (reader) {
    (async () => {
      try {
        while (isReading) {
          const { done, value } = await reader.read();
          if (done) {
            console.log('Stream complete');
            break;
          }

          // 解码并添加到缓冲区
          buffer += decoder.decode(value, { stream: true });

          // 处理缓冲区中的事件
          const lines = buffer.split('\n\n');
          buffer = lines.pop() || ''; // 保留最后一个可能不完整的事件

          for (const line of lines) {
            if (line.trim() === '' || line.startsWith(':')) continue; // 忽略空行和注释

            // 解析事件
            const eventLines = line.split('\n');
            let eventType = 'message';
            let eventData = '';

            for (const eventLine of eventLines) {
              if (eventLine.startsWith('event:')) {
                eventType = eventLine.substring(6).trim();
              } else if (eventLine.startsWith('data:')) {
                eventData = eventLine.substring(5).trim();
              }
            }

            // 创建事件对象
            const event = {
              type: eventType,
              data: eventData,
              target: eventSource
            };

            // 触发事件
            if (eventListeners[eventType]) {
              for (const listener of eventListeners[eventType]) {
                listener(event);
              }
            }

            // 触发通用消息事件
            if (eventType !== 'message' && eventSource.onmessage) {
              eventSource.onmessage({
                type: 'message',
                data: eventData,
                target: eventSource
              });
            }
          }
        }
      } catch (error) {
        console.error('Error reading stream:', error);
        // 触发错误事件
        if (eventListeners['error']) {
          for (const listener of eventListeners['error']) {
            listener({ type: 'error', error, target: eventSource });
          }
        }
      }
    })();
  }

  // 监听事件
  eventSource.onmessage = (event) => {
    console.log('SSE event:', event);
  };

  // 初始化事件 - 现在包含所有必要信息
  eventSource.addEventListener('init', (event) => {
    try {
      const data = JSON.parse(event.data) as InitEvent;
      console.log('Init event:', data);

      // 存储基本信息
      actualConversationId = data.conversationId;
      assistantMessageId = data.messageId; // Store the real assistant message ID
      userMessageId = data.userMessageId; // Store user message ID

      // 设置 parentMessageId 和处理开始时间
      if (finalAiMessageId && data.userMessageId) {
        set(messagesAtom, (prevMessages: Message[]) => {
          const messageIndex = prevMessages.findIndex(msg => msg.id === finalAiMessageId);
          if (messageIndex !== -1) {
            const newMessages = [...prevMessages];
            const messageToUpdate = newMessages[messageIndex];
            newMessages[messageIndex] = {
              ...messageToUpdate,
              parentMessageId: data.userMessageId,
              metadata: {
                ...(messageToUpdate.metadata || {}),
                status: 'loading',
                streaming: true,
                processingStartTime: data.timestamp, // 存储处理开始时间
              }
            };
            console.log(`[${streamInstanceId}] Updated message ${finalAiMessageId} with parentMessageId: ${data.userMessageId} and processingStartTime: ${data.timestamp}`);
            return newMessages;
          } else {
            console.warn(`[${streamInstanceId}] Init event: message ${finalAiMessageId} not found.`);
          }
          return prevMessages;
        });
      }

      // 如果是临时新对话，更新选中的对话ID
      if (isTempNewConversation && data.conversationId && get(selectedConversationIdAtom) === 'new') {
        set(selectedConversationIdAtom, data.conversationId);
      }
    } catch (e) {
      console.error('Failed to parse init event:', e);
    }
  });

  // 内容块开始事件 - Simplified: Placeholder removal moved to first final delta.
  eventSource.addEventListener('content_block_start', (event) => {
    try {
      const data = JSON.parse(event.data) as ContentBlockStartEvent;
      console.log('Content block start event received:', data);
      // Log tool use start if it's a tool block
      if (userMessageId && data.content_block.type === 'tool_use') {
        logStep(set, userMessageId, {
          processType: 'tool_request',
          processData: {
            toolName: data.content_block.name || 'unknown_tool',
            toolId: data.content_block.id || `tool-${Date.now()}`,
            input: data.content_block.input || {},
            _rawInputDisplay: data.content_block.input ? JSON.stringify(data.content_block.input, null, 2) : '',
            status: 'running' // Initial status
          }
        });
      }
    } catch (e) {
      console.error('Failed to parse content_block_start event:', e);
    }
  });

  // 内容块增量更新事件 (Revised according to plan)
  eventSource.addEventListener('content_block_delta', (event) => {
    try {
      // 1. 解析事件数据
      const eventData = JSON.parse(event.data) as ContentBlockDeltaEvent & { isFinal?: boolean };
      const delta = eventData.delta;
      const isFinalDelta = eventData.isFinal === true;
      const messageIdFromEvent = eventData.messageId; // Use this for potential real ID updates later
      const blockIndex = eventData.index;

      // console.log('Content block delta event:', { isFinal: isFinalDelta, delta, index: blockIndex });

      if (isFinalDelta) {
        if (delta.type === 'text_delta' && delta.text) {
          set(messagesAtom, (prevMessages: Message[]) => {
            let currentMessages = [...prevMessages];
            // Find the message using the known temporary final ID
            const finalIndex = finalAiMessageId ? currentMessages.findIndex(msg => msg.id === finalAiMessageId) : -1;

            if (finalIndex !== -1) {
              const messageToUpdate = currentMessages[finalIndex];
              const isFirstUpdate = messageToUpdate.metadata?.displayType === 'placeholder';

              // Update content
              let newContent: MessageContent[];
              if (isFirstUpdate) {
                console.log(`[${streamInstanceId}] First final delta. Updating message ${finalAiMessageId} content and displayType.`);
                newContent = [{ type: 'text', text: delta.text }];
              } else {
                // Append text
                const currentContent = Array.isArray(messageToUpdate.content) ? [...messageToUpdate.content] : [{ type: 'text', text: '' }];
                let lastTextBlock = currentContent.length > 0 ? currentContent[currentContent.length - 1] : null;
                if (lastTextBlock && lastTextBlock.type === 'text') {
                  lastTextBlock.text = (lastTextBlock.text || '') + delta.text;
                } else {
                  currentContent.push({ type: 'text', text: delta.text });
                }
                newContent = currentContent;
              }

              // Update the message in the array
              currentMessages[finalIndex] = {
                ...messageToUpdate,
                content: newContent,
                metadata: {
                  ...(messageToUpdate.metadata || {}),
                  status: 'loading',
                  displayType: 'final', // Mark as final display type now
                  streaming: true,
                  backendMessageId: messageIdFromEvent || messageToUpdate.metadata?.backendMessageId // Store or keep potential real ID
                }
              };
              return currentMessages;

            } else {
              console.warn(`[${streamInstanceId}] Received final delta, but message ID ${finalAiMessageId} not found.`);
              return prevMessages;
            }
          });
        } else if (delta.type !== 'text_delta') {
          console.warn('Received non-text delta marked as final, currently ignored for UI display:', delta);
          // Future: Handle final tool_use rendering if needed, e.g., showing final tool state.
        }
      } else {
        // --- 处理中间过程流 (Update messageProcessLogAtom ONLY) ---
        if (!userMessageId) {
          console.warn(`[${streamInstanceId}] Received non-final delta before userMessageId was set. Skipping log update.`);
          return;
        }

        if (delta.type === 'text_delta' && delta.text) {
          // Accumulate non-final text into the process log atom
          // console.log(`Updating non-final log for userMsg: ${userMessageId} with text: ${delta.text}`);
          updateOrAddLlmStep(set, userMessageId, delta.text);
        } else if (delta.type === 'input_json_delta' && delta.partial_json !== undefined) {
          // Update tool request input in the log
          const userMsgId = userMessageId;
          set(messageProcessLogAtom, (prev: Record<string, ProcessLogState>) => {
            const currentLog = prev[userMsgId];
            if (!currentLog || !currentLog.steps) return prev; // Ensure log exists

            const steps = currentLog.steps;
            // Find the *last* tool_request step to update its input stream
            let lastToolRequestStepIndex = -1;
            for (let i = steps.length - 1; i >= 0; i--) {
              if (steps[i].processType === 'tool_request') {
                const toolStep = steps[i] as ProcessLogStep & { processData: ToolRequestContent };
                // Check if the blockIndex matches the index of the tool_request step
                // Assuming blockIndex corresponds to the order tools are invoked in the backend state machine
                // This might need adjustment if backend indices aren't reliable or sequential for tools vs text blocks
                // For now, let's update the LATEST tool request if blockIndex isn't directly usable.
                // TODO: Clarify how blockIndex relates to tool calls vs text blocks from backend.
                lastToolRequestStepIndex = i;
                break; // Found the latest, update it
              }
            }

            if (lastToolRequestStepIndex !== -1) {
              const updatedSteps = [...steps];
              const existingStep = updatedSteps[lastToolRequestStepIndex] as ProcessLogStep & { processData: ToolRequestContent };

              // Append to _rawInputDisplay and attempt to parse
              const currentRaw = existingStep.processData._rawInputDisplay || '';
              const newRaw = currentRaw + delta.partial_json;
              let parsedInput = existingStep.processData.input || {};
              try {
                const potentialJson = newRaw.trim();
                // Try parsing only if it looks like a complete object
                if (potentialJson.startsWith('{') && potentialJson.endsWith('}')) {
                  parsedInput = JSON.parse(potentialJson);
                } else {
                  // Still accumulating or invalid JSON chunk
                }
              } catch (e) {
                // console.log('Intermediate JSON parse error during delta log update', e);
              }

              updatedSteps[lastToolRequestStepIndex] = {
                ...existingStep,
                processData: {
                  ...existingStep.processData,
                  input: parsedInput,
                  _rawInputDisplay: newRaw,
                },
                timestamp: new Date().toISOString(),
              };
              return { ...prev, [userMsgId]: { ...currentLog, steps: updatedSteps } };
            }
            console.warn(`[${streamInstanceId}] Received input_json_delta (index ${blockIndex}) but no corresponding tool_request step found in log`);
            return prev;
          });
        }
        // IMPORTANT: No changes to messagesAtom for non-final deltas
      }
    } catch (e) {
      console.error(`[${streamInstanceId}] Failed to parse content_block_delta event:`, e);
      // Log error step if userMessageId exists
      if (userMessageId) {
        logStep(set, userMessageId, {
          processType: 'process_error',
          processData: { message: `Failed to parse content_block_delta: ${e instanceof Error ? e.message : String(e)}` }
        });
      }
    }
  });

  // 消息结束事件 (Revised)
  eventSource.addEventListener('message_stop', (event) => {
    try {
      const data = JSON.parse(event.data) as MessageStopEvent;
      const realAssistantIdFromEvent = data.messageId;
      console.log(`[${streamInstanceId}] Received message_stop. Real ID: ${realAssistantIdFromEvent}`);

      // Update the ID from temp to real if applicable
      if (finalAiMessageId && finalAiMessageId !== realAssistantIdFromEvent) {
        set(messagesAtom, (prevMessages: Message[]) => {
          const tempIndex = prevMessages.findIndex(msg => msg.id === finalAiMessageId);

          if (tempIndex !== -1) {
            const newMessages = [...prevMessages];
            const messageToUpdate = newMessages[tempIndex];

            console.log(`[${streamInstanceId}] message_stop: Updating message ID from ${finalAiMessageId} to ${realAssistantIdFromEvent}`);
            newMessages[tempIndex] = {
              ...messageToUpdate,
              id: realAssistantIdFromEvent,
              metadata: {
                ...messageToUpdate.metadata,
                status: 'loading', // Keep loading until intent complete
                streaming: true,
              }
            };
            delete newMessages[tempIndex].metadata?.backendMessageId;

            // Update the tracked final ID variable
            console.log(`[${streamInstanceId}] Updating internal finalAiMessageId from ${finalAiMessageId} to ${realAssistantIdFromEvent} (via message_stop)`);
            finalAiMessageId = realAssistantIdFromEvent;

            return newMessages;
          } else {
            console.warn(`[${streamInstanceId}] message_stop: Had temp ID ${finalAiMessageId} but couldn't find message to update to ${realAssistantIdFromEvent}.`);
            return prevMessages;
          }
        });
      } else {
        console.log(`[${streamInstanceId}] message_stop: Ignoring event as finalAiMessageId is ${finalAiMessageId} (already real or null).`);
      }

    } catch (e) {
      console.error(`[${streamInstanceId}] Failed to parse message_stop event:`, e);
      if (userMessageId) {
        logStep(set, userMessageId, {
          processType: 'process_error',
          processData: { message: `Failed to parse message_stop: ${e instanceof Error ? e.message : String(e)}` }
        });
      }
    }
  });

  // 意图处理完成事件 (Revised)
  eventSource.addEventListener('intent_processing_complete', (event) => {
    try {
      const eventData = JSON.parse(event.data) as { type: string; messageId: string; timestamp: string; finalContent?: MessageContent[] };
      console.log(`[${streamInstanceId}] Received intent_processing_complete. Real ID: ${eventData.messageId}, Timestamp: ${eventData.timestamp}`);

      const realMessageId = eventData.messageId; // The final, real ID for the AI message
      const endTimestamp = eventData.timestamp;

      // Log the process completion step
      if (userMessageId) {
        logStep(set, userMessageId, {
          processType: 'process_complete',
          processData: { message: 'Processing finished.' }
        });
      }

      // Ensure final message is marked complete and calculate/store duration
      // Use the REAL message ID from the event data
      if (realMessageId) {
        set(messagesAtom, (prevMessages: Message[]) => {
          // Find message using the REAL ID from the event
          const finalIndex = prevMessages.findIndex(msg => msg.id === realMessageId);
          if (finalIndex !== -1) {
            const newMessages = [...prevMessages];
            const targetMessage = newMessages[finalIndex];

            // Calculate duration
            let durationDisplay: string | undefined = undefined;
            const startTimestamp = targetMessage.metadata?.processingStartTime;

            if (startTimestamp && endTimestamp) {
              try {
                const startTimeMs = new Date(startTimestamp).getTime();
                const endTimeMs = new Date(endTimestamp).getTime();
                if (!isNaN(startTimeMs) && !isNaN(endTimeMs)) {
                  const durationSeconds = Math.round((endTimeMs - startTimeMs) / 1000);
                  if (durationSeconds >= 0) {
                    durationDisplay = `(${durationSeconds}s)`;
                  } else {
                    console.warn(`[${streamInstanceId}] Calculated negative duration (${durationSeconds}s). Start: ${startTimestamp}, End: ${endTimestamp}`);
                  }
                } else {
                  console.warn(`[${streamInstanceId}] Invalid start or end timestamp for duration calculation.`);
                }
              } catch (e) {
                console.error(`[${streamInstanceId}] Error calculating duration:`, e);
              }
            } else {
              console.warn(`[${streamInstanceId}] Missing start (${startTimestamp}) or end (${endTimestamp}) timestamp for duration calculation.`);
            }

            // Update message metadata
            newMessages[finalIndex] = {
              ...targetMessage,
              // Optionally update content if backend provides final content in this event
              content: eventData.finalContent || targetMessage.content,
              metadata: {
                ...targetMessage.metadata,
                status: 'completed',
                streaming: true, // Keep streaming flag? Or set to false? Decide based on UI needs.
                processingDurationDisplay: durationDisplay, // Store duration
              }
            };
            delete newMessages[finalIndex].metadata?.backendMessageId; // Clean up temp field
            console.log(`[${streamInstanceId}] Marking message ${realMessageId} as completed. Duration: ${durationDisplay || 'N/A'}`);
            return newMessages;

          } else {
            console.warn(`[${streamInstanceId}] intent_processing_complete: Could not find final message with REAL ID ${realMessageId}.`);
          }
          return prevMessages; // Return unchanged if message not found
        });
      } else {
        console.warn(`[${streamInstanceId}] intent_processing_complete event received without a real messageId.`);
        // Fallback cleanup? Might be unnecessary if ID update logic is robust
        // set(messagesAtom, (prev: Message[]) => prev.filter((msg: Message) => msg.id !== INITIAL_PLACEHOLDER_ID));
      }

      // --- Optimistic Conversation List Update ---
      if (isTempNewConversation) {
        // For new conversations, fetching the list is the easiest way to get the title
        console.log('New conversation completed, fetching full conversation list.');
        set(fetchConversationsAtom);
      } else if (actualConversationId) {
        // For existing conversations, update optimistically
        console.log(`Existing conversation (${actualConversationId}) updated, updating list optimistically.`);
        set(conversationsAtom, (prevList: Conversation[] | undefined = []) => { // Add type and default value
          const currentList = prevList || []; // Ensure we work with an array
          const index = currentList.findIndex(c => c.id === actualConversationId);
          if (index > -1) {
            const newList = [...currentList]; // Use currentList
            const conversationToUpdate = newList[index];
            // Create updated object with new timestamp and potentially updated preview (if available)
            const updatedConversation: Conversation = { // Add type
              ...conversationToUpdate,
              updatedAt: new Date().toISOString(),
              // TODO: Consider adding lastMessagePreview update here if needed by UI
              // lastMessagePreview: extractPreviewFromFinalMessage(get(messagesAtom), finalAiMessageId),
            };
            newList.splice(index, 1); // Remove old item
            newList.unshift(updatedConversation); // Add updated item to the front
            return newList;
          } else {
            console.warn(`Optimistic update failed: Conversation ${actualConversationId} not found in list. Fetching full list.`);
            set(fetchConversationsAtom); // Fallback to full fetch
            return currentList; // Return original list while fetching
          }
        });
      } else {
        console.warn('intent_processing_complete: Cannot update conversation list optimistically, missing actualConversationId.');
        // Maybe fetch list as a fallback?
        // set(fetchConversationsAtom);
      }
      // -------------------------------------------

      // Close the SSE connection
      console.log(`[${streamInstanceId}] Closing SSE connection.`);
      eventSource.close();
    } catch (e) {
      console.error(`[${streamInstanceId}] Failed to parse intent_processing_complete event:`, e);
      // Log error step if userMessageId exists
      if (userMessageId) {
        logStep(set, userMessageId, {
          processType: 'process_error',
          processData: { message: `Failed to parse intent_processing_complete: ${e instanceof Error ? e.message : String(e)}` }
        });
      }
      // Close connection on error too
      console.log(`[${streamInstanceId}] Closing SSE connection due to error.`);
      eventSource.close();
    }
  });

  // 错误事件 (Revised)
  eventSource.addEventListener('error', (event) => {
    console.error(`[${streamInstanceId}] SSE error:`, event);
    let errorMessage = '发生错误，请重试。';
    // Attempt to parse specific error details if provided by backend in event.data or event.error
    // Example: if (event.data) try { errorMessage = JSON.parse(event.data).error || errorMessage; } catch {} else if (event.error) { errorMessage = event.error.message || errorMessage; }

    const timestamp = new Date().toISOString();

    // Log error step if userMessageId exists
    if (userMessageId) {
      logStep(set, userMessageId, {
        processType: 'process_error',
        processData: { message: `SSE error: ${errorMessage}` }
      });
    }

    set(messagesAtom, (prevMessages: Message[]) => {
      let targetIndex = finalAiMessageId ? prevMessages.findIndex(msg => msg.id === finalAiMessageId) : -1;
      const newMessages = prevMessages.filter(msg => msg.id !== INITIAL_PLACEHOLDER_ID); // Remove placeholder

      if (targetIndex !== -1) {
        // Error occurred after final message started: update the final message
        const targetMessage = newMessages[targetIndex];
        // Ensure content is an array before pushing
        const contentArray = Array.isArray(targetMessage.content) ? targetMessage.content : [{ type: 'text', text: String(targetMessage.content) || '' }];
        contentArray.push({ type: 'text', text: `\n\n[系统错误]: ${errorMessage}` });
        newMessages[targetIndex] = {
          ...targetMessage,
          content: contentArray,
          metadata: { ...(targetMessage.metadata || {}), status: 'error' },
          timestamp: targetMessage.timestamp || timestamp // Keep original timestamp if possible
        };
      } else {
        // Error occurred before final message started: add a new error message
        console.log("Error before final message started. Adding new error message.");
        const errorMsg: Message = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: [{ type: 'text', text: `[系统错误]: ${errorMessage}` }],
          timestamp: timestamp,
          parentMessageId: userMessageId || undefined,
          metadata: { status: 'error' }
        };
        newMessages.push(errorMsg);
      }
      return newMessages;
    });
    eventSource.close();
  });

  // 意图处理错误事件 (Revised)
  eventSource.addEventListener('intent_processing_error', (event) => {
    console.error(`[${streamInstanceId}] Intent processing error event:`, event);
    let errorMessage = '处理您的请求时发生错误，请重试。';
    try {
      const errorData = JSON.parse(event.data);
      if (errorData.error) errorMessage = String(errorData.error); // Ensure string
    } catch (e) { /* ignore parse error, use default message */ }

    const timestamp = new Date().toISOString();

    // Log error step if userMessageId exists
    if (userMessageId) {
      logStep(set, userMessageId, {
        processType: 'process_error',
        processData: { message: `Intent processing error: ${errorMessage}` }
      });
    }

    set(messagesAtom, (prevMessages: Message[]) => {
      let targetIndex = finalAiMessageId ? prevMessages.findIndex(msg => msg.id === finalAiMessageId) : -1;
      const newMessages = prevMessages.filter(msg => msg.id !== INITIAL_PLACEHOLDER_ID); // Remove placeholder

      if (targetIndex !== -1) {
        // Error occurred after final message started: update the final message
        const targetMessage = newMessages[targetIndex];
        const contentArray = Array.isArray(targetMessage.content) ? targetMessage.content : [{ type: 'text', text: String(targetMessage.content) || '' }];
        contentArray.push({ type: 'text', text: `\n\n[处理错误]: ${errorMessage}` });
        newMessages[targetIndex] = {
          ...targetMessage,
          content: contentArray,
          metadata: { ...(targetMessage.metadata || {}), status: 'error' },
          timestamp: targetMessage.timestamp || timestamp
        };
      } else {
        // Error occurred before final message started: add a new error message
        console.log("Intent processing error before final message started. Adding new error message.");
        const errorMsg: Message = {
          id: `error-${Date.now()}`,
          role: 'assistant',
          content: [{ type: 'text', text: `[处理错误]: ${errorMessage}` }],
          timestamp: timestamp,
          parentMessageId: userMessageId || undefined,
          metadata: { status: 'error' }
        };
        newMessages.push(errorMsg);
      }
      return newMessages;
    });
    eventSource.close();
  });

  // 工具调用开始事件 is handled by content_block_start for tool_use

  // 工具调用结果事件 (Updates logAtom ONLY)
  eventSource.addEventListener('tool_result', (event) => {
    try {
      const data = JSON.parse(event.data) as ToolResultEvent;
      console.log('Tool result event:', data);
      if (userMessageId && data.toolId) { // Ensure toolId exists
        // Log the result summary step
        logStep(set, userMessageId, {
          processType: 'tool_result_summary',
          processData: {
            toolName: data.toolName || 'Unknown Tool',
            toolId: data.toolId,
            status: 'success',
            message: `Tool ${data.toolName || 'Unknown'} executed successfully.`, // Standardized message
            // Summarize result briefly. Avoid storing large raw data here.
            data: typeof data.result === 'string' ? { summary: data.result.substring(0, 200) + (data.result.length > 200 ? '...' : '') } : { summary: 'Structured result received' }
          }
        });
        // Update the corresponding tool_request step's status to complete
        updateToolRequestStepStatus(set, userMessageId, data.toolId, 'complete');
      } else {
        console.warn('Tool result event received without userMessageId or toolId:', data);
      }
    } catch (e) {
      console.error('Failed to parse tool_result event:', e);
      // Log error step if userMessageId exists
      if (userMessageId) {
        logStep(set, userMessageId, {
          processType: 'process_error',
          processData: { message: `Failed to parse tool_result: ${e instanceof Error ? e.message : String(e)}`, toolId: (event.data && JSON.parse(event.data)?.toolId) || 'unknown' }
        });
        // Optionally mark the tool as error if toolId is known
        const toolId = (event.data && JSON.parse(event.data)?.toolId);
        if (toolId) updateToolRequestStepStatus(set, userMessageId, toolId, 'error');
      }
    }
  });

  // 新增: 工具调用错误事件 (Updates logAtom ONLY)
  // This might be redundant if backend sends tool errors via standard 'error' or 'tool_result' with error status
  eventSource.addEventListener('tool_error', (event) => {
    try {
      const data = JSON.parse(event.data) as ToolResultEvent & { error: string }; // Assume error structure
      console.error('Tool error event:', data);
      if (userMessageId && data.toolId) { // Ensure toolId exists
        logStep(set, userMessageId, {
          processType: 'tool_result_summary', // Log as a result summary indicating error
          processData: {
            toolName: data.toolName || 'Unknown Tool',
            toolId: data.toolId,
            status: 'error',
            message: data.error || `Tool ${data.toolName || 'Unknown'} failed`,
          }
        });
        updateToolRequestStepStatus(set, userMessageId, data.toolId, 'error');
      } else {
        console.warn('Tool error event received without userMessageId or toolId:', data);
        // Log generic error if possible
        if (userMessageId) {
          logStep(set, userMessageId, {
            processType: 'process_error',
            processData: { message: `Received tool_error event with missing IDs: ${JSON.stringify(data)}` }
          });
        }
      }
    } catch (e) {
      console.error('Failed to parse tool_error event:', e);
      if (userMessageId) {
        logStep(set, userMessageId, {
          processType: 'process_error',
          processData: { message: `Failed to parse tool_error: ${e instanceof Error ? e.message : String(e)}` }
        });
      }
    }
  });

  // 工具调用结束事件 (Updates logAtom ONLY)
  // This event might be less critical if tool_result/tool_error already handles status updates
  eventSource.addEventListener('tool_use_stop', (event) => {
    try {
      const data = JSON.parse(event.data) as ToolUseStopEvent;
      console.log('Tool use stop event:', data);
      // Ensure corresponding tool_request status is marked complete if still running
      if (userMessageId && data.toolId) {
        updateToolRequestStepStatus(set, userMessageId, data.toolId, 'complete', true); // Only update if 'running'
      } else {
        console.warn('Tool use stop event received without userMessageId or toolId:', data);
      }
    } catch (e) {
      console.error('Failed to parse tool_use_stop event:', e);
      if (userMessageId) {
        logStep(set, userMessageId, {
          processType: 'process_error',
          processData: { message: `Failed to parse tool_use_stop: ${e instanceof Error ? e.message : String(e)}`, toolId: (event.data && JSON.parse(event.data)?.toolId) || 'unknown' }
        });
      }
    }
  });



  // Return structure needs adjustment as streamingContainerMessage is removed
  // We return the placeholder ID initially. The caller should rely on state updates.
  return {
    conversation: { id: actualConversationId || (isTempNewConversation ? 'new' : currentConversationId) || '' },
    userMessage: { id: userMessageId || userMessage.id },
    aiMessageId: INITIAL_PLACEHOLDER_ID as string, // Return placeholder ID
    conversationId: actualConversationId || (isTempNewConversation ? 'new' : currentConversationId) || ''
  };
};